import { Box, Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AggregatedStat, DonationForm } from '@feathr/blackbox';
import { CardV2 as Card, SegmentedControl } from '@feathr/components';

import { buildBreakdownData, type TDateRange } from '../DonationFormReport.utils';
import DonationAmountTrendChart from './DonationAmountTrendChart';
import DonationCountTrendChart from './DonationCountTrendChart';
import { useDonationFormTrends } from './DonationFormTrends.hooks';
import {
  buildDonationAmountChartData,
  buildDonationCountChartData,
  buildViewsChartData,
} from './DonationFormTrends.utils';
import DonationTypeDistributionChart from './DonationTypeDistributionChart';
import ViewsOverTimeChart from './ViewsOverTimeChart';

import * as styles from './DonationFormTrends.css';

const TRENDS_TABS = ['views', 'donations', 'breakdown'] as const;
type TTrendsTab = (typeof TRENDS_TABS)[number];

const DEFAULT_TAB: TTrendsTab = 'views';

function isTrendsTab(value: string): value is TTrendsTab {
  return (TRENDS_TABS as readonly string[]).includes(value);
}

export interface IDonationFormTrendsProps {
  /** The donation form being reported on. */
  form: DonationForm;
  /** The resolved `[startISO, endISO]` range for the current window. */
  range: TDateRange;
  /** The resolved `[startISO, endISO]` range for the immediately preceding window. */
  previousRange: TDateRange;
  /** Aggregated stats for the current window. Drives the Breakdown tab. */
  currentStats: AggregatedStat | undefined;
}

function DonationFormTrends({
  form,
  range,
  previousRange,
  currentStats,
}: Readonly<IDonationFormTrendsProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [activeTab, setActiveTab] = useState<TTrendsTab>(DEFAULT_TAB);
  const { currentDailyStats, previousDailyStats } = useDonationFormTrends({
    form,
    range,
    previousRange,
  });

  const segmentData = useMemo(
    () => [
      { label: t('Views & Engagement'), value: 'views' },
      { label: t('Donations'), value: 'donations' },
      { label: t('Breakdown'), value: 'breakdown' },
    ],
    [t],
  );

  // Recompute on every render — observer() tracks the underlying MobX array,
  // and useMemo would miss in-place mutations because the array reference stays stable.
  const viewsChartData = buildViewsChartData(
    currentDailyStats.models,
    previousDailyStats.models,
    range,
    previousRange,
  );
  const amountChartData = buildDonationAmountChartData(
    currentDailyStats.models,
    previousDailyStats.models,
    range,
    previousRange,
  );
  const countChartData = buildDonationCountChartData(
    currentDailyStats.models,
    previousDailyStats.models,
    range,
    previousRange,
  );

  function handleTabChange(value: string): void {
    if (isTrendsTab(value)) {
      setActiveTab(value);
    }
  }

  return (
    <Card contentClassName={styles.root} width={'full'}>
      <Card.Header title={t('Trends')} width={'full'} />

      <Card.Content>
        <Stack align={'stretch'} gap={theme.spacing['6']}>
          <Box className={styles.segmentedControlWrapper}>
            <SegmentedControl
              data={segmentData}
              name={'donation-form-trends-tab'}
              onChange={handleTabChange}
              value={activeTab}
            />
          </Box>
          {activeTab === 'views' && <ViewsOverTimeChart data={viewsChartData} />}
          {activeTab === 'donations' && (
            <Box className={styles.donationsGrid}>
              <DonationAmountTrendChart data={amountChartData} />
              <DonationCountTrendChart data={countChartData} />
            </Box>
          )}
          {activeTab === 'breakdown' && (
            <DonationTypeDistributionChart data={buildBreakdownData(currentStats)} />
          )}
        </Stack>
      </Card.Content>
    </Card>
  );
}

export default observer(DonationFormTrends);
